import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/cn";

export type ToastVariant = "default" | "success" | "warning" | "destructive";

export interface ToastAction {
  readonly label: string;
  readonly onClick: () => void;
}

export interface ToastOptions {
  readonly title: string;
  readonly description?: string;
  readonly variant?: ToastVariant;
  /** Auto-dismiss delay in ms. Use `0` to keep the toast until dismissed. */
  readonly durationMs?: number;
  readonly action?: ToastAction;
}

export interface ToastRecord {
  readonly id: string;
  readonly title: string;
  readonly description?: string;
  readonly variant: ToastVariant;
  readonly durationMs: number;
  readonly action?: ToastAction;
}

export interface ToastContextValue {
  readonly toasts: readonly ToastRecord[];
  /** Queues a toast; returns its generated id. */
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

const VARIANT_ICON: Record<ToastVariant, React.ComponentType<{ className?: string }>> = {
  default: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  destructive: CircleAlert,
};

const VARIANT_CLASS: Record<ToastVariant, string> = {
  default: "border-border bg-popover text-popover-foreground",
  success: "border-border bg-popover text-popover-foreground",
  warning: "border-warning/60 bg-warning/15 text-warning-foreground",
  destructive: "border-destructive/50 bg-destructive/10 text-destructive",
};

function buildRecord(id: string, options: ToastOptions): ToastRecord {
  const base = {
    id,
    title: options.title,
    variant: options.variant ?? "default",
    durationMs: options.durationMs ?? 5000,
  };
  return {
    ...base,
    ...(options.description !== undefined ? { description: options.description } : {}),
    ...(options.action !== undefined ? { action: options.action } : {}),
  };
}

export interface ToastItemProps {
  readonly toast: ToastRecord;
  readonly onDismiss: (id: string) => void;
}

export function ToastItem({ toast, onDismiss }: ToastItemProps): React.JSX.Element {
  const Icon = VARIANT_ICON[toast.variant];
  return (
    <div
      role="status"
      data-variant={toast.variant}
      className={cn(
        "pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border p-4 shadow-lg",
        VARIANT_CLASS[toast.variant],
      )}
    >
      <Icon aria-hidden className="mt-0.5 size-4 shrink-0" />
      <div className="flex-1 space-y-1">
        <p className="text-sm font-medium">{toast.title}</p>
        {toast.description !== undefined ? (
          <p className="text-muted-foreground text-sm">{toast.description}</p>
        ) : null}
        {toast.action !== undefined ? (
          <button
            type="button"
            className="focus-visible:ring-ring text-sm font-medium underline focus-visible:ring-2 focus-visible:outline-none"
            onClick={toast.action.onClick}
          >
            {toast.action.label}
          </button>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Dismiss notification"
        className="focus-visible:ring-ring rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:outline-none"
        onClick={() => onDismiss(toast.id)}
      >
        <X aria-hidden className="size-4" />
      </button>
    </div>
  );
}

export function ToastViewport({
  toasts,
  onDismiss,
}: {
  readonly toasts: readonly ToastRecord[];
  readonly onDismiss: (id: string) => void;
}): React.JSX.Element {
  return (
    <div
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4 sm:items-end"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

export function ToastProvider({
  children,
}: {
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const [toasts, setToasts] = React.useState<readonly ToastRecord[]>([]);
  const counter = React.useRef(0);
  const timers = React.useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const toast = React.useCallback(
    (options: ToastOptions): string => {
      counter.current += 1;
      const id = `toast-${counter.current}`;
      const record = buildRecord(id, options);
      setToasts((prev) => [...prev, record]);
      if (record.durationMs > 0) {
        const timer = setTimeout(() => dismiss(id), record.durationMs);
        timers.current.set(id, timer);
      }
      return id;
    },
    [dismiss],
  );

  React.useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = React.useMemo<ToastContextValue>(
    () => ({ toasts, toast, dismiss }),
    [toasts, toast, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = React.useContext(ToastContext);
  if (context === null) {
    throw new Error("useToast must be used within <ToastProvider>");
  }
  return context;
}
